import { useMemo, useState } from 'react';
import { X, Search } from 'lucide-react';
import useAuthStore from '../../store/authStore';
import { ASSIGNABLE_LEVELS, stripOwnerOnlyCapabilities } from './constants';
import CapabilitiesChecklist from './CapabilitiesChecklist';
import useAllWorkspaces from './useAllWorkspaces';
import { foldText } from '../../utils/fold';

const SCOPE_OPTIONS = [
  { value: 'all_workspaces', label: 'Todas as áreas de trabalho' },
  { value: 'selected', label: 'Áreas selecionadas' },
  { value: 'none', label: 'Nenhuma (só por exceção de quadro)' },
];

export default function RoleFormModal({ role, capabilitiesCatalog, onClose, onSubmit, onDelete }) {
  const currentUser = useAuthStore((s) => s.user);
  const allowedKeys = Array.isArray(currentUser?.capabilities) ? new Set(currentUser.capabilities) : null;

  const isEditing = !!role;
  const [name, setName] = useState(role?.name || '');
  const [defaultLevel, setDefaultLevel] = useState(role?.default_level || 'editor');
  const [scopeMode, setScopeMode] = useState(role?.scope_mode || 'selected');
  const [workspaceIds, setWorkspaceIds] = useState(() => new Set(role?.workspace_ids || []));
  // Filtra já na entrada: um grupo legado (ex.: de sistema) pode ter vindo do
  // backend com org.reset/org.transfer_ownership marcadas de antes da
  // revisão de segurança — a checklist nunca deve exibi-las como concedidas.
  const [capabilities, setCapabilities] = useState(() => stripOwnerOnlyCapabilities(role?.capabilities));
  const [wsSearch, setWsSearch] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const { workspaces: allWorkspaces, loading: loadingWorkspaces } = useAllWorkspaces();
  const filteredWorkspaces = useMemo(() => {
    const q = foldText(wsSearch.trim());
    const list = q ? allWorkspaces.filter((w) => foldText(w.name).includes(q)) : allWorkspaces;
    return [...list].sort((a, b) => (a.name || '').localeCompare(b.name || ''));
  }, [allWorkspaces, wsSearch]);

  const toggleWorkspace = (id) => setWorkspaceIds((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      await onSubmit({
        name: name.trim(),
        default_level: defaultLevel,
        scope_mode: scopeMode,
        workspace_ids: scopeMode === 'selected' ? Array.from(workspaceIds) : [],
        // Defesa em profundidade: mesmo que o estado carregue org.reset /
        // org.transfer_ownership (não deveria, já são filtradas na entrada e
        // a checklist bloqueia marcá-las), nunca envia — são exclusivas do
        // Proprietário e a API recusa com 403 para qualquer outro grupo.
        capabilities: stripOwnerOnlyCapabilities(capabilities),
      });
      onClose();
    } catch (err) {
      if (err.status === 403) {
        setError(err.message || 'Permissão negada: alguma capability enviada é exclusiva do Proprietário ou excede o que seu grupo pode conceder.');
      } else {
        setError(err.message || 'Falha ao salvar grupo');
      }
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    setError(null);
    try {
      await onDelete(role);
      onClose();
    } catch (err) {
      setError(err.message || 'Falha ao excluir grupo');
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <form
        onSubmit={handleSubmit}
        onClick={(e) => e.stopPropagation()}
        className="bg-card rounded-xl border border-border shadow-2xl w-[560px] max-h-[85vh] flex flex-col"
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-border/50 shrink-0">
          <h3 className="text-sm font-semibold text-text-primary">
            {isEditing ? `Editar grupo — ${role.name}` : 'Novo grupo de permissão'}
          </h3>
          <button type="button" onClick={onClose} className="text-text-secondary hover:text-text-primary">
            <X className="w-4 h-4" />
          </button>
        </div>

        {error && (
          <div className="px-5 py-2.5 border-b border-danger/30 bg-danger/10 text-xs text-danger shrink-0">
            {error}
          </div>
        )}

        <div className="p-5 space-y-4 overflow-y-auto">
          <div>
            <label className="block text-xs text-text-secondary mb-1">Nome</label>
            <input
              required autoFocus value={name} onChange={(e) => setName(e.target.value)}
              className="w-full bg-main border border-border rounded-lg px-3 py-2 text-sm text-text-primary outline-none focus:border-accent/50"
            />
          </div>

          <div>
            <label className="block text-xs text-text-secondary mb-1">Nível padrão</label>
            <select
              value={defaultLevel}
              onChange={(e) => setDefaultLevel(e.target.value)}
              className="w-full bg-main border border-border rounded-lg px-3 py-2 text-sm text-text-primary outline-none focus:border-accent/50"
            >
              {ASSIGNABLE_LEVELS.map((l) => (
                <option key={l.value} value={l.value}>{l.label}</option>
              ))}
            </select>
            <p className="text-[11px] text-text-secondary mt-1">
              Nível que uma pessoa deste grupo recebe nas áreas do escopo abaixo, quando não houver exceção.
            </p>
          </div>

          <div>
            <label className="block text-xs text-text-secondary mb-1">Escopo</label>
            <div className="space-y-1.5">
              {SCOPE_OPTIONS.map((opt) => (
                <label key={opt.value} className="flex items-center gap-2 text-sm text-text-primary cursor-pointer">
                  <input
                    type="radio"
                    name="scope_mode"
                    checked={scopeMode === opt.value}
                    onChange={() => setScopeMode(opt.value)}
                    className="accent-accent"
                  />
                  {opt.label}
                </label>
              ))}
            </div>

            {scopeMode === 'selected' && (
              <div className="mt-2 border border-border/50 rounded-lg overflow-hidden">
                <div className="px-2 py-1.5 border-b border-border/50 relative">
                  <Search className="w-3.5 h-3.5 absolute left-4 top-1/2 -translate-y-1/2 text-text-secondary" />
                  <input
                    value={wsSearch}
                    onChange={(e) => setWsSearch(e.target.value)}
                    placeholder="Buscar área…"
                    className="w-full bg-main border border-border rounded pl-7 pr-2 py-1.5 text-xs text-text-primary outline-none focus:border-accent/50"
                  />
                </div>
                <div className="max-h-40 overflow-y-auto py-1">
                  {loadingWorkspaces && <div className="px-3 py-2 text-xs text-text-secondary">Carregando…</div>}
                  {!loadingWorkspaces && filteredWorkspaces.length === 0 && (
                    <div className="px-3 py-2 text-xs text-text-secondary">Nenhuma área encontrada.</div>
                  )}
                  {filteredWorkspaces.map((w) => (
                    <label key={w.id} className="flex items-center gap-2 px-3 py-1.5 text-xs text-text-primary hover:bg-white/[0.03] cursor-pointer">
                      <input
                        type="checkbox"
                        checked={workspaceIds.has(w.id)}
                        onChange={() => toggleWorkspace(w.id)}
                        className="w-3.5 h-3.5 accent-accent"
                      />
                      <span className="truncate">{w.name}</span>
                    </label>
                  ))}
                </div>
                <div className="px-3 py-1.5 border-t border-border/50 text-[11px] text-text-secondary">
                  {workspaceIds.size} {workspaceIds.size === 1 ? 'área selecionada' : 'áreas selecionadas'}
                </div>
              </div>
            )}
          </div>

          <div>
            <label className="block text-xs text-text-secondary mb-2">Capabilities</label>
            <CapabilitiesChecklist
              catalog={capabilitiesCatalog}
              selected={capabilities}
              allowedKeys={allowedKeys}
              onChange={setCapabilities}
            />
          </div>
        </div>

        <div className="px-5 py-3 border-t border-border/50 flex items-center justify-between shrink-0">
          <div>
            {isEditing && !role.locked && role.system_key !== 'owner' && onDelete && (
              <button
                type="button"
                onClick={handleDelete}
                className="px-3 py-2 text-xs text-danger hover:bg-danger/10 rounded-lg transition-colors"
              >
                Excluir grupo
              </button>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-text-secondary hover:text-text-primary">
              Cancelar
            </button>
            <button
              type="submit"
              disabled={saving || !name.trim()}
              className="px-4 py-2 text-sm font-medium bg-accent text-white rounded-lg hover:bg-accent/80 transition-colors disabled:opacity-40"
            >
              {saving ? 'Salvando...' : 'Salvar'}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}
